import React, { Fragment, useState } from 'react';

import { useIntl } from 'react-intl';

import { cssMap } from '@atlaskit/css';
import ChevronDownIcon from '@atlaskit/icon/core/chevron-down';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import CrossCircleIcon from '@atlaskit/icon/core/cross-circle';
import SuccessIcon from '@atlaskit/icon/core/status-success';
import { Box, Inline, Pressable, Stack, Text } from '@atlaskit/primitives/compiled';
import { token } from '@atlaskit/tokens';

import { ErrorBoundary } from '../../../common/ui/error-boundary';
import { messages } from '../messages';
import { PluginOutputRenderer } from '../plugin-output-renderer';
import {
	type ParsedPluginOutput,
	type PluginExtraInfo,
	type PluginInvocation,
	PluginInvocationStatus,
	PluginName,
} from '../types';
import { parsePluginOutput } from '../utils';

type ParsedPluginData = {
	extraInfo: PluginExtraInfo | string | null | undefined;
	parsedOutput: ParsedPluginOutput | null;
};

type ParsePluginExtraInfoParams = {
	pluginName: string | undefined;
	pluginExtraInfo: string | undefined;
	pluginOutput: string | undefined;
};

/**
 * Parses plugin data to extract displayable information.
 *
 * Different plugin types have different data structures:
 *
 * 1. Search Plugin:
 *    - Uses plugin_extra_info containing searchResults/rankedDocuments as JSON
 *    - Falls back to raw string if JSON parsing fails
 *
 * 2. Other Plugins (Teamwork, People, Page Search, etc.):
 *    - Uses plugin_output containing structured text
 *    - Attempts to parse output using parsePluginOutput for structured display
 */
const parsePluginExtraInfo = ({
	pluginName,
	pluginExtraInfo,
	pluginOutput,
}: ParsePluginExtraInfoParams): ParsedPluginData => {
	let extraInfo: PluginExtraInfo | string | null | undefined;
	let parsedOutput: ParsedPluginOutput | null = null;

	// Search plugin: parse plugin_extra_info for search results
	if (pluginName === PluginName.SEARCH) {
		try {
			if (pluginExtraInfo) {
				extraInfo = JSON.parse(pluginExtraInfo);
			}
		} catch {
			extraInfo = pluginExtraInfo;
		}
	}
	// Other plugins: parse plugin_output for structured display
	else {
		extraInfo = pluginOutput;
		if (typeof extraInfo === 'string') {
			parsedOutput = parsePluginOutput(pluginName, extraInfo);
		}
	}

	return { extraInfo, parsedOutput };
};

const pluginInvocationStyles = cssMap({
	pressable: {
		paddingTop: token('space.100'),
		paddingRight: token('space.100'),
		paddingBottom: token('space.100'),
		paddingLeft: token('space.100'),
		cursor: 'pointer',
		width: '100%',
	},
	content: {
		paddingTop: token('space.200'),
		paddingRight: token('space.200'),
		paddingBottom: token('space.200'),
		paddingLeft: token('space.200'),
		backgroundColor: token('color.background.neutral.subtle'),
	},
});

export const PluginInvocationItem = ({
	plugin,
	index,
}: {
	plugin: PluginInvocation;
	index: number;
}): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const [isExpanded, setIsExpanded] = useState(false);

	const isSuccess = plugin.plugin_invocation_status === PluginInvocationStatus.PLUGIN_FINISHED;
	const StatusIcon = isSuccess ? SuccessIcon : CrossCircleIcon;
	const statusColor = isSuccess ? token('color.icon.success') : token('color.icon.danger');
	const statusText = isSuccess
		? formatMessage(messages.ai_debugger_modal_success_label)
		: formatMessage(messages.ai_debugger_modal_failed_label);
	const expandLabel = formatMessage(messages.ai_debugger_modal_expand_label);
	const collapseLabel = formatMessage(messages.ai_debugger_modal_collapse_label);

	const { extraInfo, parsedOutput } = parsePluginExtraInfo({
		pluginName: plugin.plugin_name,
		pluginExtraInfo: plugin.plugin_extra_info,
		pluginOutput: plugin.plugin_output,
	});

	return (
		<ErrorBoundary fallback={<Fragment />}>
			<Box>
				<Pressable
					onClick={() => setIsExpanded(!isExpanded)}
					xcss={pluginInvocationStyles.pressable}
				>
					<Inline space="space.100" alignBlock="center" spread="space-between">
						<Inline space="space.100" alignBlock="center">
							{isExpanded ? (
								<ChevronDownIcon label={collapseLabel} color="currentColor" />
							) : (
								<ChevronRightIcon label={expandLabel} color="currentColor" />
							)}
							<Text weight="medium">{plugin.plugin_name || `Plugin ${index + 1}`}</Text>
						</Inline>
						<Inline space="space.050" alignBlock="center">
							<StatusIcon label={statusText} color={statusColor} />
							<Text color={isSuccess ? 'color.text.success' : 'color.text.danger'}>
								{statusText}
							</Text>
						</Inline>
					</Inline>
				</Pressable>
				{isExpanded && (
					<Box xcss={pluginInvocationStyles.content}>
						<Stack space="space.200">
							<PluginOutputRenderer parsedOutput={parsedOutput} extraInfo={extraInfo} />
						</Stack>
					</Box>
				)}
			</Box>
		</ErrorBoundary>
	);
};
